import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  keepPreviousData,
  queryOptions,
  skipToken,
  useQuery,
} from '@tanstack/react-query';
import type { SortingState } from '@tanstack/react-table';
import type { Polygon } from 'geojson';
import { HTTPError } from 'ky';
import { useAtomValue } from 'jotai';
import type { MapDataEvent } from 'mapbox-gl';
import { useMap } from 'react-map-gl';

import { useSettledValue } from 'lib/hooks/use-settled-value';
import { api } from 'lib/request/api';
import { errorBodyMessage } from 'utils/api-errors';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';

import type { BBox } from 'components/query-map';

import { tractsSourceHrefAtom } from './atoms';
import type {
  BoundingBox,
  MapFilterMode,
  MapQueryFilters,
  MapQueryFiltersChange,
  MapQueryFit,
  MapQueryListedShapes,
  MapQuerySearchParams,
  MapQueryTractsResponse,
  TractSource,
} from './types';
import { spatialParam } from './utils';

/** How long the filters must sit still before they are searched. */
export const MAP_QUERY_SEARCH_DEBOUNCE_MS = 500;

/**
 * Posts one Map Query search. A refusal's reasons (`{ errors: [...] }`) become the
 * error's message.
 *
 * @param {MapQuerySearchParams} params - The search
 * @param {AbortSignal} signal - Aborts it when a newer search replaces it
 * @return {Promise<MapQueryTractsResponse>} The page, facets and map counts
 */
const searchMapQueryTracts = async (
  params: MapQuerySearchParams,
  signal: AbortSignal
): Promise<MapQueryTractsResponse> => {
  try {
    return await api
      .post('queries/packages/map_query_tracts', { json: params, signal })
      .json<MapQueryTractsResponse>();
  } catch (error) {
    if (!(error instanceof HTTPError)) throw error;
    const body: unknown = await error.response.json().catch(() => null);
    throw new Error(errorBodyMessage(body, 'The search failed. Try again.'), {
      cause: error,
    });
  }
};

/**
 * The Map Query search for the current filters, sent once they have been still
 * for {@link MAP_QUERY_SEARCH_DEBOUNCE_MS}. A newer search aborts the one in
 * flight (React Query cancels the query it leaves), and the last answer stays
 * shown while the next one loads.
 *
 * @param {MapQuerySearchParams} params - The search, memoized by the caller
 * @return {object} The settled search's data and the search it answers
 *   (`answered`, behind `params` while the next one loads), the error, and the
 *   visible extent the search was sent (`searchedExtent`, null under another
 *   map filter), for the tract tiles to ask for the same
 */
export const useMapQuerySearch = (params: MapQuerySearchParams) => {
  const settled = useSettledValue(params, MAP_QUERY_SEARCH_DEBOUNCE_MS);
  const { spatial } = settled;
  const query = useQuery(
    queryOptions({
      queryKey: ['packages', 'map-query-tracts', settled],
      queryFn: async ({ signal }) => ({
        params: settled,
        response: await searchMapQueryTracts(settled, signal),
      }),
      placeholderData: keepPreviousData,
    })
  );
  return {
    data: query.data?.response,
    answered: query.data?.params,
    error: query.error,
    searchedExtent:
      spatial && 'boundingBox' in spatial ? spatial.boundingBox : null,
  };
};

/**
 * The newest shapes of the Tracts a list adds, for the map: the tract tiles draw
 * only the Tracts the filters match. Fetched when the listed Tracts change, not
 * with the filters; the last shapes stay drawn while the next ones load.
 *
 * @param {string[]} tractIds - The listed Tracts, memoized by the caller
 * @return {object} The shapes, null with no listed Tract, and whether they load
 */
export const useListedTractShapes = (tractIds: string[]) => {
  const listed = tractIds.length > 0;
  const query = useQuery(
    queryOptions({
      queryKey: ['packages', 'map-query-listed-shapes', tractIds],
      queryFn: ({ signal }) =>
        api
          .post('queries/packages/map_query_listed_shapes', {
            json: { tractIds },
            signal,
          })
          .json<MapQueryListedShapes>(),
      enabled: listed,
      placeholderData: keepPreviousData,
    })
  );
  return {
    shapes: listed ? (query.data ?? null) : null,
    loading: query.isFetching,
  };
};

/** The parts of a TileJSON the tract layer reads. */
interface TileJson {
  tiles: string[];
  minzoom?: number;
}

/**
 * The tracts map source, read from its TileJSON as the app's other map layers
 * read theirs, and not read again while held: null until it has loaded, and
 * when it could not be read.
 *
 * @return {TractSource | null} The tile URL template and the source's minzoom
 */
export const useTractSource = (): TractSource | null => {
  const href = useAtomValue(tractsSourceHrefAtom);
  const { data } = useQuery(
    queryOptions({
      queryKey: ['maps', 'sources', 'tracts', href],
      queryFn: href
        ? ({ signal }) =>
            api.get(href.replace(/^\//, ''), { signal }).json<TileJson>()
        : skipToken,
      staleTime: Infinity,
      select: ({ tiles, minzoom }: TileJson): TractSource => ({
        tiles: tiles[0],
        minzoom: minzoom ?? 0,
      }),
    })
  );
  return data ?? null;
};

/**
 * The map's zoom, kept current as the user zooms. For a child of `<Map>`.
 *
 * @return {number | null} The zoom, or null before the map exists
 */
export const useMapZoom = (): number | null => {
  const { current: map } = useMap();
  const [zoom, setZoom] = useState<number | null>(() => map?.getZoom() ?? null);
  useEffect(() => {
    if (!map) return undefined;
    /** Reads the zoom the map is at now. */
    const update = () => setZoom(map.getZoom());
    update();
    map.on('zoomend', update);
    return () => {
      map.off('zoomend', update);
    };
  }, [map]);
  return zoom;
};

/**
 * Whether the map is fetching tiles of a source: from a tile request until the
 * map has drawn all it asked for. For a child of `<Map>`.
 *
 * @param {string} sourceId - The source
 * @return {boolean} True while its tiles load
 */
export const useSourceLoading = (sourceId: string): boolean => {
  const { current: map } = useMap();
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!map) return undefined;
    /**
     * Notes a tile request of the source.
     *
     * @param {MapDataEvent} event - Any data the map starts loading
     */
    const start = (event: MapDataEvent) => {
      if ('sourceId' in event && event.sourceId === sourceId) setLoading(true);
    };
    /** The map has drawn everything it asked for. */
    const done = () => setLoading(false);
    map.on('dataloading', start);
    map.on('idle', done);
    return () => {
      map.off('dataloading', start);
      map.off('idle', done);
    };
  }, [map, sourceId]);
  return loading;
};

/**
 * The box to fit the map to for these filters, once their search has answered
 * (`resultsTotal` known): null before or for other filters. The server sends
 * none where the map does not fit (no filter, the visible extent); none either
 * while a polygon's corners are being edited, which the camera would keep moving
 * under.
 *
 * @param {number | null} resultsTotal - These filters' total, null until it answers
 * @param {MapQueryTractsResponse} [data] - The answer on hand
 * @param {string} filtersKey - `resultsKey` of the filters
 * @param {boolean} aoiEditing - A polygon's corners are being edited
 * @return {MapQueryFit | null} The fit, or null for none
 */
export const useResultsFit = (
  resultsTotal: number | null,
  data: MapQueryTractsResponse | undefined,
  filtersKey: string,
  aoiEditing: boolean
): MapQueryFit | null => {
  const bbox: BBox | null =
    resultsTotal !== null ? (data?.meta.resultsBbox ?? null) : null;
  return useMemo(
    () => (bbox && !aoiEditing ? { key: filtersKey, bbox } : null),
    [bbox, aoiEditing, filtersKey]
  );
};

/**
 * The Map Filter section's state: the mode, the last visible extent the map
 * reported and the drawn polygon (the form's), whether its corners are being
 * edited, and the search's map area from them. A drawn polygon outlives a switch
 * of mode, so it applies again back on Draw AOI Polygon; a switch of mode ends
 * vertex editing.
 *
 * @param {MapQueryFilters} filters - The form's Map Query filters
 * @param {MapQueryFiltersChange} onFiltersChange - Changes them
 * @return {object} The state, the map area and the handlers
 */
export const useMapFilter = (
  { mode, extent, polygon }: MapQueryFilters,
  onFiltersChange: MapQueryFiltersChange
) => {
  const [aoiEditing, setAoiEditing] = useState(false);
  const spatial = useMemo(
    () => spatialParam(mode, extent, polygon),
    [mode, extent, polygon]
  );

  const changeMode = useCallback(
    (next: MapFilterMode) => {
      onFiltersChange((current) => ({ ...current, mode: next }));
      setAoiEditing(false);
    },
    [onFiltersChange]
  );
  // The map reports the extent on every move; the form keeps an unmoved one as it
  // was, so it searches nothing new.
  const changeExtent = useCallback(
    (next: BoundingBox) =>
      onFiltersChange((current) => ({ ...current, extent: next })),
    [onFiltersChange]
  );
  const setPolygon = useCallback(
    (next: Polygon | null) =>
      onFiltersChange((current) => ({ ...current, polygon: next })),
    [onFiltersChange]
  );
  const clearPolygon = useCallback(() => {
    setPolygon(null);
    setAoiEditing(false);
  }, [setPolygon]);

  return {
    mode,
    extent,
    polygon,
    aoiEditing,
    spatial,
    changeMode,
    changeExtent,
    setPolygon,
    setAoiEditing,
    clearPolygon,
  };
};

/** The search's own order, shown on the Asset Name header. */
const DEFAULT_SORTING: SortingState = [{ id: 'assetName', desc: false }];

/**
 * The results table's page and sort. New filters (a change of `filtersKey`) and a
 * new sort show their results from the first page; the page size stays.
 *
 * @param {string} filtersKey - Changes whenever the filters do
 * @return {object} The page, the sort and their setters
 */
export const useResultsPaging = (filtersKey: string) => {
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [sorting, setSortingState] = useState(DEFAULT_SORTING);
  const [pagedFilters, setPagedFilters] = useState(filtersKey);
  if (pagedFilters !== filtersKey) {
    // Adjusting state to a changed input during render, as React advises,
    // so no request goes out for the old page.
    setPagedFilters(filtersKey);
    setPagination((current) =>
      current.page === 1 ? current : { ...current, page: 1 }
    );
  }

  const setSorting = useCallback((next: SortingState) => {
    setSortingState(next);
    setPagination((current) => ({ ...current, page: 1 }));
  }, []);

  return { pagination, setPagination, sorting, setSorting };
};
